import {
    ClipboardCheck,
    UtensilsCrossed,
    Cog,
    GraduationCap,
    FlaskConical,
    Handshake,
    Award,
} from "lucide-react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/primitives";
import { CTABand } from "../components/CTABand";

const AREAS = [
    {
        icon: ClipboardCheck,
        title: "Sustainability Assessment",
        desc: "Help hospitality businesses understand their current sustainability practices and identify areas for improvement.",
    },
    {
        icon: UtensilsCrossed,
        title: "Food-Waste Reduction",
        desc: "Help hotels identify practical opportunities to prevent, measure and reduce food waste.",
    },
    {
        icon: Cog,
        title: "Sustainable Operations",
        desc: "Support practical improvements in areas such as energy, water, waste, procurement and resource efficiency.",
    },
    {
        icon: GraduationCap,
        title: "Training & Education",
        desc: "Develop sustainability learning and training for hospitality employees and leaders.",
    },
    {
        icon: FlaskConical,
        title: "Research & Innovation",
        desc: "Conduct and support research into practical hospitality sustainability solutions.",
    },
    {
        icon: Handshake,
        title: "Partnerships",
        desc: "Connect hotels with researchers, technology providers, tourism organizations, communities and sustainability partners.",
    },
    {
        icon: Award,
        title: "Recognition",
        desc: "Develop credible ways to recognize measurable sustainability progress as GGH grows.",
    },
];

const WhatWeDo = () => (
    <main>
        <SEO
            title="What We Do | Global Green Hotel"
            description="Sustainability assessment, food-waste reduction, sustainable operations, training, research, partnerships and recognition for the hospitality industry."
        />
        <PageHero
            testid="what-we-do-hero"
            eyebrow="What We Do"
            titleLines={["Practical work,", "measurable outcomes"]}
            description="GGH focuses on the areas where sustainability, hospitality operations and business performance meet."
        />

        <section data-testid="what-we-do-areas" className="py-24 sm:py-32">
            <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="areas-grid">
                    {AREAS.map(({ icon: Icon, title, desc }, i) => (
                        <Reveal
                            key={title}
                            delay={0.06 * i}
                            y={24}
                            className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}
                        >
                            <article
                                data-testid={`area-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                className="card-lift group flex h-full flex-col rounded-2xl border border-[#E8E6E1] bg-white p-8 transition-colors duration-300 hover:border-[#1A3B2B]"
                            >
                                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#1A3B2B] text-[#D9B96A] transition-colors duration-300 group-hover:bg-[#274E3A]">
                                    <Icon size={24} aria-hidden="true" />
                                </span>
                                <h3 className="font-display mt-8 text-2xl font-medium tracking-tight text-[#0A1711]">
                                    {title}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-[#4A5D54]">{desc}</p>
                            </article>
                        </Reveal>
                    ))}
                    <Reveal delay={0.45} y={24} className="sm:col-span-2">
                        <div className="grain relative flex h-full flex-col justify-center overflow-hidden rounded-2xl bg-[#04150D] p-8 sm:p-10">
                            <Eyebrow dark>A Note on Credibility</Eyebrow>
                            <p className="font-display mt-4 text-xl italic leading-relaxed text-[#FDFBF7] sm:text-2xl">
                                GGH is building carefully. We do not currently provide official
                                certification — recognition programs will be developed as GGH
                                grows, and only where they can be credible.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>

        <CTABand
            testid="cta-band-what-we-do"
            heading="Want to Work With GGH?"
            text="Tell us about your hotel, organization or idea — and let's explore where sustainability can create real value."
            primary={{ to: "/contact", label: "Contact GGH" }}
            secondary={{ to: "/for-hotels", label: "For Hotels" }}
        />
    </main>
);

export default WhatWeDo;
